import type { ReactNode } from 'react';
import { AuthProvider, useAuth } from '../features/onboarding/AuthContext';
import { OnboardingStateProvider } from '../features/onboarding/useOnboardingState';
import { ApiKeysProvider } from '../features/onboarding/ApiKeysContext';

/**
 * Bridges `AuthContext`'s `user` into `OnboardingStateProvider`'s `uid`
 * prop — a separate component because `useAuth()` can only be called from
 * a descendant of `AuthProvider`, and `OnboardingStateProvider` itself
 * deliberately takes `uid` as a plain prop (not by calling `useAuth()`
 * internally) so it stays unit-testable without a Firebase mock.
 */
function OnboardingStateBridge({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const uid = user === undefined ? undefined : (user?.uid ?? null);
  return <OnboardingStateProvider uid={uid}>{children}</OnboardingStateProvider>;
}

/** Composes every app-wide context provider in the order each depends on the last (TTP-243). */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <AuthProvider>
      <OnboardingStateBridge>
        <ApiKeysProvider>{children}</ApiKeysProvider>
      </OnboardingStateBridge>
    </AuthProvider>
  );
}
